import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type { Base, PortraitCardKind, WeeklyCrossingDto } from '../../contracts/api'
import { Collapsible } from '../components/Collapsible'
import { JournalMark } from '../components/JournalMark'
import { SystemIcon } from '../components/SystemIcon'
import { useNav } from '../nav'
import { haptic } from '../telegram'
import { WEEKLY_LABEL } from './Today'
import './crossings.css'

interface CrossingsResponse {
  current: WeeklyCrossingDto | null
  history: WeeklyCrossingDto[]
}

/** Цвет подписи: согласие — зелёное, спор — бордовое. Тот же язык, что в «Я». */
const ACCENT: Record<PortraitCardKind, string> = {
  support: 'var(--green)',
  contradiction: 'var(--bordo)',
  condition: 'var(--ochre)',
  unclear: 'var(--text3)',
}

/** `2026-W36` → «неделя 36»; чужой год дописываем, иначе история врёт. */
function weekNote(week: string): string {
  const match = /^(\d{4})-W(\d{1,2})$/.exec(week)
  if (!match) return week
  const year = match[1] ?? ''
  const number = Number(match[2])
  const label = `неделя ${number}`
  return year === String(new Date().getFullYear()) ? label : `${label}, ${year}`
}

/**
 * Основания пересечения, но кликабельные: пересечение — единственное место,
 * где человек хочет спросить «а откуда это вообще», и ответ лежит в комнате
 * расчётов. И-Цзин комнаты не имеет — его строка остаётся обычной.
 */
function Evidence({ items }: { items: Base[] }) {
  const { navigate } = useNav()
  if (items.length === 0) return null
  return (
    <div className="bases">
      {items.map((base) => {
        const system = base.system
        const body = (
          <span>
            {base.label}
            {base.detail && <span className="base-detail"> — {base.detail}</span>}
          </span>
        )
        if (system === 'iching') {
          return (
            <div className="base-row" key={base.label}>
              <SystemIcon system={system} />
              {body}
            </div>
          )
        }
        return (
          <button
            type="button"
            className="base-row base-row-tap"
            key={base.label}
            onClick={() => {
              haptic('light')
              navigate({ screen: { kind: 'room', system } })
            }}
          >
            <SystemIcon system={system} />
            {body}
            <span className="base-go" aria-hidden="true">
              ›
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * «Пересечения» — где системы говорят об одном и где спорят. Одно пересечение
 * на неделю: карточка недели сверху, всё прошлое — свёрнутыми строками. Смысл
 * в накоплении, поэтому под текущим пересечением стоит отметка журнала.
 */
export function Crossings() {
  const [data, setData] = useState<CrossingsResponse | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .get<CrossingsResponse>('/api/crossings')
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'Пересечения не открылись')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Смотрим, что сошлось…</div>

  const { current, history } = data

  return (
    <div className="screen crossings">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">пересечения</div>
      </div>

      <h1 className="display">Пересечения</h1>
      <div className="sub">Где системы говорят об одном и где спорят. Одно за неделю — остальное копится ниже.</div>

      {current ? (
        <>
          <div className="card crossing-card" style={{ boxShadow: `inset 3px 0 0 ${ACCENT[current.kind]}` }}>
            <div className="lbl" style={{ color: ACCENT[current.kind] }}>
              {WEEKLY_LABEL[current.kind]}
            </div>
            <p className="crossing-title">{current.title}</p>
            {current.text && (
              <>
                <p className="crossing-text">{current.text.now}</p>
                <div className="day-block">
                  <div className="lbl">Проверь на этой неделе</div>
                  <p>{current.text.check}</p>
                </div>
              </>
            )}
            <Evidence items={current.evidence} />
          </div>

          <div className="card crossing-journal">
            <div className="lbl">Сошлось?</div>
            <p className="crossing-jnote">Отметка остаётся в журнале — через месяц будет видно, что повторяется.</p>
            <JournalMark
              kind="week"
              refKey={`${current.week}:${current.title}`}
              ref={{ week: current.week, title: current.title, kind: current.kind }}
              keys={[]}
            />
          </div>
        </>
      ) : (
        <div className="card crossing-empty">
          <div className="lbl">Пока пусто</div>
          <p>Первое пересечение появится, когда ты откроешь «Сегодня».</p>
        </div>
      )}

      {history.length > 0 && (
        <>
          <div className="lbl sect">Было раньше</div>
          <div className="rowlist crossing-history">
            {history.map((item) => (
              <Collapsible key={item.week} title={item.title} note={weekNote(item.week)}>
                <div className="lbl" style={{ color: ACCENT[item.kind] }}>
                  {WEEKLY_LABEL[item.kind]}
                </div>
                {item.text && (
                  <>
                    <p className="crossing-past-text">{item.text.now}</p>
                    <p className="crossing-past-check">Проверял: {item.text.check}</p>
                  </>
                )}
                <Evidence items={item.evidence} />
              </Collapsible>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
